import {
  buildClipContext,
  buildStoryVideoContext,
  buildTemplateChange,
} from '@src/analytics/eventPayloadHelpers';
import {
  trackVideoTemplateApplied,
  type VideoTemplateActionSource,
} from '@src/analytics/EventTracker';
import {
  InlineRenameField,
  PENCIL_REVEAL,
} from '@src/components/common/InlineRenameField';
import { useFeatureFlags } from '@src/stores-v2/StoreContext';
import BackIcon from '@src/svgs/BackIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import GearIcon from '@src/svgs/GearIcon';
import RefreshIcon from '@src/svgs/RefreshIcon';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import styled from 'styled-components';

import {
  DRAFT_TEMPLATE_LABEL,
  useVideoTemplatesContext,
  VideoTemplateTab,
} from './VideoTemplatesContext';
import { VideoTemplateSettings } from './VideoTemplateSettings';
import { VideoTemplatesGrid } from './VideoTemplatesGrid';

import type { VideoTemplateConfigured } from './types';

export const VideoTemplates = observer(
  ({
    disabled,
    actionSource = 'creator_studio',
    clipId,
    clipTitle,
  }: {
    disabled?: boolean;
    actionSource?: VideoTemplateActionSource;
    clipId?: string;
    clipTitle?: string;
  }) => {
    const {
      currentTab,
      activeTemplate,
      activeTemplateSettings,
      setCurrentTab,
      clearTemplate,
      videoTemplates,
      addVideoTemplate,
      editor,
      videoCreator,
      hasAccessToEditAll,
      hasAccessToEditThisTemplate,
      createNewTemplate,
      renameTemplate,
    } = useVideoTemplatesContext();
    const [isCreating, setIsCreating] = useState(false);
    const [isPlacementModalOpen, setIsPlacementModalOpen] = useState(false);
    const { videoTemplatesHideSettings } = useFeatureFlags();
    // Settings requires template definition, Clear only requires template elements to exist
    const hasTemplateApplied = !!activeTemplateSettings;
    const canRenameTemplate =
      !disabled && (hasAccessToEditAll || hasAccessToEditThisTemplate);

    const handleAddVideoTemplate = (template: VideoTemplateConfigured) => {
      if (disabled) return;
      trackVideoTemplateApplied({
        action_source: actionSource,
        ...buildStoryVideoContext(videoCreator),
        ...buildClipContext(clipId, clipTitle),
        ...buildTemplateChange(
          { id: activeTemplate?.id, name: activeTemplate?.label },
          { id: template.id, name: template.label },
        ),
      });
      addVideoTemplate(template);
    };

    // Creating runs several Dato round trips; guard against a second click
    // spawning a second draft template.
    const handleCreateNew = async () => {
      if (isCreating) return;
      setIsCreating(true);
      try {
        await createNewTemplate();
      } finally {
        setIsCreating(false);
      }
    };

    return (
      <Container>
        {currentTab === VideoTemplateTab.GRID && (
          <VideoTemplatesGridContainer>
            <VideoTemplatesGrid
              videoTemplates={videoTemplates}
              onAddVideoTemplate={handleAddVideoTemplate}
              onSettingsClick={
                videoTemplatesHideSettings
                  ? undefined
                  : () =>
                      hasTemplateApplied &&
                      !disabled &&
                      setCurrentTab(VideoTemplateTab.SETTINGS)
              }
              onClearClick={
                hasTemplateApplied && !disabled ? clearTemplate : undefined
              }
              activeTemplateId={activeTemplate?.id}
              disabled={disabled}
              onCreateNew={hasAccessToEditAll ? handleCreateNew : undefined}
              isCreating={isCreating}
            />
          </VideoTemplatesGridContainer>
        )}

        {currentTab === VideoTemplateTab.SETTINGS && (
          <>
            <HeaderSection>
              <TitleRow>
                <BackButton
                  onClick={() =>
                    !disabled && setCurrentTab(VideoTemplateTab.GRID)
                  }
                  disabled={disabled}
                >
                  <BackIcon width="14" height="10" strokeColor="currentColor" />
                </BackButton>
                {activeTemplate &&
                  (canRenameTemplate ? (
                    <InlineRenameField
                      value={activeTemplate.label}
                      placeholder={DRAFT_TEMPLATE_LABEL}
                      onCommit={label =>
                        renameTemplate(activeTemplate.id, label)
                      }
                      ariaLabel={`Rename ${activeTemplate.label}`}
                    />
                  ) : (
                    <SectionTitle>{activeTemplate.label}</SectionTitle>
                  ))}
              </TitleRow>
              <ActionsRow>
                <ActionButton
                  onClick={() =>
                    !disabled && addVideoTemplate(activeTemplate!, true)
                  }
                  disabled={disabled || !activeTemplate}
                >
                  <RefreshIcon
                    width="12"
                    height="12"
                    strokeColor="currentColor"
                  />
                  Reset
                </ActionButton>
                <ActionButton
                  onClick={() => !disabled && clearTemplate()}
                  disabled={disabled}
                >
                  <CloseIcon
                    width="10"
                    height="10"
                    strokeColor="currentColor"
                  />
                  Clear
                </ActionButton>
                {!videoTemplatesHideSettings &&
                  (hasAccessToEditAll || activeTemplate) && (
                    <ActionButton
                      onClick={() => !disabled && setIsPlacementModalOpen(true)}
                      disabled={disabled}
                    >
                      <GearIcon width="12" height="12" color="currentColor" />
                      Settings
                    </ActionButton>
                  )}
              </ActionsRow>
            </HeaderSection>
            <VideoTemplateSettings
              editor={editor}
              videoCreator={videoCreator}
              compact
              disabled={disabled}
              isPlacementModalOpen={isPlacementModalOpen}
              onClosePlacementModal={() => setIsPlacementModalOpen(false)}
            />
          </>
        )}
      </Container>
    );
  },
);

const Container = styled.div`
  /* The panel card's body has no top padding, so without this both views — the
     template grid and the selected-template header — sit flush against the
     header divider. */
  padding-top: var(--spacing-md);
  /* No bottom spacing here: the panel card's Body already contributes
     --spacing-md, which is the whole of what the Speakers panel uses and is
     the pattern every tab now follows. Adding more here double-counted it. */
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const VideoTemplatesGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  /* InlineRenameField hides its pencil until the host reveals it. */
  &:hover {
    ${PENCIL_REVEAL}: 1;
  }
`;

const BackButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius);
  padding: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: all 150ms ease-out;
  color: var(--text-muted);

  &:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;

const SectionTitle = styled.div`
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  letter-spacing: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const ActionButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  border-radius: var(--radius);
  padding: 4px 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: all 150ms ease-out;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);

  &:hover {
    background-color: var(--surface-elevated);
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;
